import React, { useState } from "react";
import {
  StyledTable,
  TableContainer,
} from "../../../components/tables/styledComponents";
import { CustomTableCell } from "../../../components/tables/CustomTableCell";
import {
  CellType,
  EngagementDataSourceType,
} from "../../../models/tableModels";
import { ENGAGEMENT_BASE_COLUMNS } from "../constants";
import {
  buildFlagOptions,
  FLAG_COLOR_TO_STATUS,
  FLAG_STATUS_TO_FLAG_KEY,
} from "../../../components/visuals/StatusFlag";
import {
  applyFilter,
  getColumnTypeFromKeyOrValue,
  getUniqueFilters,
} from "../tableUtils";
import { useHandleFlagChange } from "../../../hooks/useHandleFlagChange";
import { CustomDropdownProps } from "../../../components/visuals/PageFilter/PageFilter";
import { TABLE_BASE_HEIGHT } from "../../../styles";
import { FlagColor, FlagStatus, MilestoneKey } from "../../../enums/Status";
import { MESSAGES } from "../../../messages";
import { EngagementItem } from "../../../models/stewardshipModels";
import { FlagKey, SubjectType } from "../../../models/workflowModels";
import { Filter } from "../../../types/PageFilterTypes";

import m1Icon from "../../../assets/svg/m1_icon.svg";
import m2Icon from "../../../assets/svg/m2_icon.svg";
import m3Icon from "../../../assets/svg/m3_icon.svg";
import m4Icon from "../../../assets/svg/m4_icon.svg";
import ciIcon from "../../../assets/svg/ci_icon.svg";

const MILESTONE_ICONS: Record<MilestoneKey, string> = {
  [MilestoneKey.M1]: m4Icon,
  [MilestoneKey.M2]: m3Icon,
  [MilestoneKey.M3]: m2Icon,
  [MilestoneKey.M4]: m1Icon,
  [MilestoneKey.CI]: ciIcon,
};

export const EngagementTable: React.FC<{ data: EngagementItem[] }> = ({
  data,
}) => {
  const { handleFlagChange } = useHandleFlagChange(
    SubjectType.ENGAGEMENT_RECORDS,
  );

  const [flagOptions, setFlagOptions] = useState<{
    [key: string]: Filter[];
  } | null>(null);

  const dataWithFlag: EngagementDataSourceType[] = Array.from(
    new Map(
      data.map((item) => [
        item.engagement_id,
        {
          ...item,
          flagsDropdown: {
            options: buildFlagOptions(item.flagStatus),
            isInTable: true,
          },
        },
      ]),
    ).values(),
  );

  const injectValues = (
    dataIndex: string,
    value: CellType,
    record: EngagementDataSourceType,
  ): CellType => {
    switch (dataIndex) {
      case "flagsDropdown": {
        const flags = value as CustomDropdownProps;
        flags.onSelectOption = updateFlag;

        const newOptions =
          (flagOptions && flagOptions[record.engagement_id]) || flags.options;
        newOptions.forEach((option) => {
          option.selectorName = record.engagement_id;
        });

        flags.options = newOptions;

        return flags;
      }

      case "milestone_description": {
        const milestoneKey = record.milestone_id as MilestoneKey;

        return {
          username: milestoneKey,
          image: MILESTONE_ICONS[milestoneKey],
          displayText: value as string,
        };
      }

      case "manager_name": {
        return {
          username: (value as string) || "",
          image: record.manager_image || null,
          displayText: value as string,
        };
      }

      default:
        return value || MESSAGES.NOT_AVAILABLE;
    }
  };

  const updateFlag = (flagOption: Filter): void => {
    handleFlagChange(flagOption);
    const flagStatus: FlagStatus =
      FLAG_COLOR_TO_STATUS[flagOption.key as FlagColor];
    const flagKey: FlagKey = FLAG_STATUS_TO_FLAG_KEY[flagStatus];
    setFlagOptions((prev) => ({
      ...prev,
      [flagOption.selectorName as string]: buildFlagOptions(flagKey),
    }));
  };

  return (
    <TableContainer>
      <StyledTable
        dataSource={dataWithFlag}
        pagination={{ pageSize: 30 }}
        hasEveryCellBorderBottom={true}
        scroll={{ y: TABLE_BASE_HEIGHT, x: "max-content" }}
      >
        {ENGAGEMENT_BASE_COLUMNS.map((column) => (
          <StyledTable.Column
            key={column.key}
            dataIndex={column.dataIndex}
            title={column.title}
            fixed={column.fixed}
            filters={
              column.hasFilters
                ? getUniqueFilters(
                    dataWithFlag,
                    column.dataIndex as keyof EngagementDataSourceType,
                  )
                : undefined
            }
            // @ts-expect-error onFilter is not expecting a custom function
            onFilter={
              column.hasFilters
                ? (
                    value: CellType,
                    record: EngagementDataSourceType,
                  ): boolean =>
                    applyFilter(
                      record,
                      value as string,
                      column.dataIndex as keyof EngagementDataSourceType,
                    )
                : null
            }
            render={(
              value: CellType,
              record: EngagementDataSourceType,
            ): JSX.Element => (
              <CustomTableCell
                value={injectValues(column.dataIndex as string, value, record)}
                type={
                  getColumnTypeFromKeyOrValue(column.key as string, value).type
                }
              />
            )}
          />
        ))}
      </StyledTable>
    </TableContainer>
  );
};
